import React, { useEffect, useLayoutEffect, useState } from "react";
import {} from "prop-types";
import {
  ADMIN_PAGESIZE_OPTIONS,
  APP_TITLE_SUFFIX,
  DEFAULT_PAGE_SIZE,
} from "@constants";
import {
  Container,
  Dropdown,
  FormControl,
  InputGroup,
  Modal,
  Row,
  Stack,
} from "react-bootstrap";
import { Paginator, TableCustom } from "@components";
import clsx from "clsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import styles from "./styles/ManageCourses.module.css";
import { useDispatch, useSelector } from "react-redux";
import { exploreCourseAction } from "@redux/actions";
import { formatNumberText } from "@helpers";
import { EditModal } from "./EditModal";
import { showFailureToast } from "@helpers";
import { debounce } from "lodash";
import { useCallback } from "react";

const ManageCourses = () => {
  // state
  const [pageSize, setPageSize] = useState(ADMIN_PAGESIZE_OPTIONS[1]);
  const [keyword, setKeyword] = useState("");
  const [target, setTarget] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // variables
  let pagination = null;
  let resultSummary = "";

  // logics before hooks

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { exploreCourse } = useSelector((state) => state.course);

  // logics
  const viewCourse = (courseId) =>
    navigate(`/admin-course-detail-view/${courseId}`);

  const handleCloseEditModal = () => setEditModalVisible(false);

  const editCourse = (courseId) => {
    const target = exploreCourse.data?.find((one) => one.id === courseId);
    if (!target) {
      showFailureToast("Something'wrong, course not found!");
    } else {
      setTarget(target);
      setEditModalVisible(true);
    }
  };
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Author",
      dataIndex: "user",
      render: (user) => (
        // <Link to={"/"} className="text-decoration-none">
        //   {user.fullName}
        // </Link>
        <div>{user.fullName}</div>
      ),
    },
    {
      title: "Publish Date",
      dataIndex: "createdAt",
      render: (publishDate) => {
        return (
          <div>{`${moment(publishDate)
            .add(7, "hours")
            .format("DD MMMM YYYY | HH:mm")}`}</div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (category) => (
        // <Link to={"/"} className="text-decoration-none">
        //   {category.name}
        // </Link>
        <div>{category.name}</div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        let statusName;
        let statusVariant;
        switch (status) {
          case 0:
            statusName = "Pending";
            statusVariant = "primary";
            break;
          case 1:
            statusName = "Approved";
            statusVariant = "success";
            break;
          case 2:
            statusName = "Rejected";
            statusVariant = "warning";
            break;
          case 3:
            statusName = "Blocked";
            statusVariant = "danger";
            break;
        }
        return (
          <strong className={`text-${statusVariant} mr-2`}>{statusName}</strong>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (id) => (
        <div>
          <i
            className={clsx("bi bi-eye mr-2", styles.icon)}
            onClick={() => {
              viewCourse(id);
            }}
          />
          <i
            className={clsx("bi bi-pencil-square", styles.icon)}
            onClick={() => {
              editCourse(id);
            }}
          />
        </div>
      ),
    },
  ];

  if (exploreCourse.pagination) {
    const { CurrentPage, PageSize, TotalCount } = exploreCourse.pagination;
    const start = (CurrentPage - 1) * PageSize + 1;
    const end = CurrentPage * PageSize;
    resultSummary = TotalCount
      ? `Showing ${start} to ${
          end < TotalCount ? end : TotalCount
        } of total ${formatNumberText(TotalCount, "course")}`
      : "No Result";
    pagination = <Paginator data={{ ...exploreCourse.pagination }} />;
  }
  const search = useCallback(
    debounce((Keyword) => {
      if (
        !Keyword ||
        Keyword.replace(/[^\w\s']|_/g, "")
          .replace(/\s+/g, " ")
          .trim()
      ) {
        searchParams.set("Keyword", Keyword);
        searchParams.set("PageNumber", 1);
        searchParams.sort();
        setSearchParams(searchParams, { replace: true });
      }
    }, 500),
    []
  );

  const handlePageSizeChange = (option) => {
    setPageSize(option);
    searchParams.set("PageSize", option.value);
    searchParams.set("PageNumber", 1);
    searchParams.sort();
    setSearchParams(searchParams, { replace: true });
  };

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
    search(event.target.value);
  };

  // UI partials

  const searchBox = (
    <InputGroup>
      <InputGroup.Text className={styles.searchBoxPrefix}>
        <i className="bi bi-search" />
      </InputGroup.Text>
      <FormControl
        type="search"
        placeholder="Search"
        value={keyword}
        onChange={handleKeywordChange}
      />
    </InputGroup>
  );

  // effects
  useEffect(() => {
    const Keyword = (searchParams.get("Keyword") || "")
      .replace(/\s+/g, " ")
      .trim();
    const PageSize = Number(searchParams.get("PageSize")) || DEFAULT_PAGE_SIZE;
    const PageNumber = Number(searchParams.get("PageNumber")) || 1;
    dispatch(
      exploreCourseAction({
        Keyword,
        Orderby: "createdAt desc",
        PageSize,
        PageNumber,
        IsActive: true,
      })
    );
  }, [dispatch, searchParams]);

  useLayoutEffect(() => {
    const Keyword = searchParams.get("Keyword") || "";
    document.title = `Manage Courses | ${APP_TITLE_SUFFIX}`;
    const pageSize = ADMIN_PAGESIZE_OPTIONS.find(
      (one) =>
        one.value ===
        (Number(searchParams.get("PageSize")) ||
          ADMIN_PAGESIZE_OPTIONS[1].value)
    );
    setKeyword(Keyword);
    setPageSize(pageSize);
  }, []);

  // render
  return (
    <Container fluid>
      <Stack direction="horizontal" className="h5 my-4">
        <i className="bi bi-collection-play mr-2" /> Manage Courses
      </Stack>
      {searchBox}
      <div className="d-flex justify-content-between align-items-center py-3">
        <Dropdown>
          <Dropdown.Toggle
            id="sortPopover"
            variant="light"
            className="border"
            disabled={exploreCourse.processing}
          >
            Show <strong>{pageSize.label}</strong>
          </Dropdown.Toggle>
          <Dropdown.Menu variant="light" className="w-100">
            {ADMIN_PAGESIZE_OPTIONS.map((option) => (
              <Dropdown.Item
                className={clsx(
                  styles.option,
                  pageSize.value === option.value ? styles.active : ""
                )}
                key={option.id}
                active={pageSize.value === option.value}
                onClick={() => handlePageSizeChange(option)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <strong>{resultSummary}</strong>
      </div>
      <Row>
        <TableCustom
          indexing
          columns={columns}
          dataSource={exploreCourse.data ?? []}
          isLoading={exploreCourse.processing}
        />
      </Row>
      <div className="d-flex justify-content-center">{pagination}</div>;
      <Modal show={editModalVisible} onHide={handleCloseEditModal}>
        <EditModal data={target} handleClose={handleCloseEditModal} />
      </Modal>
    </Container>
  );
};

/* required: display name */
ManageCourses.displayName = "ManageCourses";

/* required: props types */
ManageCourses.propTypes = {};

/* required: default props */
ManageCourses.defaultProps = {};

export { ManageCourses };
